import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Home, BookOpen, Target, TrendingUp, User, Menu, BarChart3, Sun, Moon, FileText, Shield, Swords, Handshake, Bookmark, Edit } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';

const Layout = ({ children }) => {
  const { user, isAuthenticated, logout } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const location = useLocation();

  const navItems = [
    { icon: Home, label: 'Home', path: '/dashboard', authRequired: true },
    { icon: BookOpen, label: 'Exams', path: '/exams', authRequired: false },
    { icon: FileText, label: 'Test', path: '/test/create', authRequired: false },
    { icon: Target, label: 'Practice', path: '/practice', authRequired: false },
    { icon: TrendingUp, label: 'Mistakes', path: '/mistakes', authRequired: true, premium: true },
    { icon: Bookmark, label: 'Bookmarks', path: '/bookmarks', authRequired: true },
    { icon: Swords, label: 'Challenge a Friend', path: '/challenges', authRequired: true, premium: true },
    { icon: Handshake, label: 'Practice with Friend', path: '/practice-friend', authRequired: true, premium: true },
    { icon: BarChart3, label: 'Analytics', path: '/analytics', authRequired: true, premium: true },
    { icon: Edit, label: 'Editor', path: '/editor', authRequired: true, editorOnly: true },
    { icon: Shield, label: 'Admin', path: '/admin', authRequired: true, adminOnly: true },
  ];

  const filteredNavItems = navItems.filter(item => {
    // Admin-only items: only show to admin role
    if (item.adminOnly && user?.role !== 'admin') return false;
    // Editor-only items: show to content_editor or admin role
    if (item.editorOnly && user?.role !== 'content_editor' && user?.role !== 'admin') return false;
    return !item.authRequired || isAuthenticated;
  });

  const NavLinks = () => (
    <>
      {filteredNavItems.map((item) => {
        const Icon = item.icon;
        const isActive = location.pathname === item.path;
        return (
          <Link
            key={item.path}
            to={item.path}
            data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
            className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 group ${
              isActive
                ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg'
                : 'text-muted-foreground hover:bg-blue-500/20 hover:text-foreground'
            }`}
          >
            <Icon size={20} className={isActive ? 'text-white' : 'group-hover:text-blue-400'} />
            <span className={`font-medium ${isActive ? 'text-white' : ''}`}>{item.label}</span>
            {item.premium && !user?.premium && (
              <span className="ml-auto text-[10px] px-2 py-0.5 bg-gradient-to-r from-blue-500 to-cyan-400 text-white rounded-full font-bold shadow-md shadow-blue-500/30">
                PRO
              </span>
            )}
            {item.editorOnly && (
              <span className="ml-auto text-[10px] px-2 py-0.5 bg-gradient-to-r from-orange-500 to-amber-400 text-white rounded-full font-bold shadow-md shadow-orange-500/30">
                EDITOR
              </span>
            )}
            {item.adminOnly && (
              <span className="ml-auto text-[10px] px-2 py-0.5 bg-gradient-to-r from-red-500 to-rose-400 text-white rounded-full font-bold shadow-md shadow-red-500/30">
                ADMIN
              </span>
            )}
          </Link>
        );
      })}
    </>
  );

  return (
    <div className="min-h-screen bg-background theme-transition">
      {/* Desktop Sidebar - Cozy Blue */}
      <aside className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col border-r border-border bg-card theme-transition">
        <div className="flex flex-col flex-grow pt-6 pb-4 overflow-y-auto">
          <div className="flex items-center justify-between flex-shrink-0 px-6 mb-8">
            <Link 
              to={isAuthenticated ? "/dashboard" : "/"} 
              className="flex items-center gap-3 hover:opacity-90 transition-all group" 
              data-testid="logo-link"
            >
              <img src="/logo192.png" alt="ExamDex" className="w-12 h-12 object-contain scale-150 group-hover:scale-[1.6] transition-all" />
              <div>
                <span className="text-2xl font-heading font-bold text-foreground">ExamDex</span>
              </div>
            </Link>
          </div>
          
          <nav className="flex-1 px-3 space-y-1">
            <NavLinks />
          </nav>
          
          {/* Theme Toggle */}
          <div className="px-3 py-3 border-t border-border">
            <button
              onClick={toggleTheme}
              data-testid="theme-toggle"
              className="w-full flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-blue-500/20 transition-all duration-200 text-muted-foreground hover:text-foreground font-medium"
            >
              {isDark ? <Sun size={20} className="text-blue-400" /> : <Moon size={20} className="text-blue-500" />}
              <span className="font-medium">{isDark ? 'Light Mode' : 'Dark Mode'}</span>
            </button>
          </div>
          
          {isAuthenticated && (
            <div className="px-3 pt-3 border-t border-border">
              <Link
                to="/profile"
                data-testid="profile-link"
                className="flex items-center gap-3 px-4 py-3 rounded-lg bg-gradient-to-br from-blue-500/10 to-blue-400/5 border border-blue-500/30 hover:border-blue-500/60 transition-all duration-200 group"
              >
                <div className="w-11 h-11 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center group-hover:shadow-lg group-hover:shadow-blue-500/50 transition-all">
                  <User size={22} className="text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-foreground truncate">{user?.name}</p>
                  <p className="text-xs text-muted-foreground">{user?.points || 0} XP</p>
                </div>
              </Link>
              <Button
                variant="ghost"
                onClick={logout}
                data-testid="logout-button"
                className="w-full mt-3 text-red-400 hover:bg-red-500/10 font-semibold"
              >
                Logout
              </Button>
            </div>
          )}
          
          {!isAuthenticated && (
            <div className="px-3 pt-3 border-t border-border">
              <Link to="/auth?mode=login">
                <Button className="w-full bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-bold shadow-lg rounded-lg">
                  Sign In
                </Button>
              </Link>
            </div>
          )}
        </div>
      </aside>

      {/* Mobile Header - Cozy Blue */}
      <header className="lg:hidden sticky top-0 z-50 bg-card/95 backdrop-blur-xl border-b border-border theme-transition">
        <div className="flex items-center justify-between px-4 py-4">
          <Link 
            to={isAuthenticated ? "/dashboard" : "/"} 
            className="flex items-center gap-2 hover:opacity-90 transition-all" 
            data-testid="mobile-logo-link"
          >
            <img src="/logo192.png" alt="ExamDex" className="w-10 h-10 object-contain scale-150" />
            <span className="text-lg font-heading font-bold text-foreground">ExamDex</span>
          </Link>
          <div className="flex items-center gap-2">
            <button
              onClick={toggleTheme}
              className="p-2.5 rounded-lg hover:bg-blue-500/20 transition-colors"
            >
              {isDark ? <Sun size={20} className="text-blue-400" /> : <Moon size={20} className="text-blue-500" />}
            </button>
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" data-testid="mobile-menu-button" className="hover:bg-blue-500/20">
                  <Menu size={24} className="text-foreground" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-64 bg-card border-l border-border">
                <nav className="flex flex-col gap-2 mt-8">
                  <NavLinks />
                </nav>
                {isAuthenticated ? (
                  <div className="mt-8 pt-4 border-t border-border">
                    <Link
                      to="/profile"
                      data-testid="mobile-profile-link"
                      className="flex items-center gap-3 px-4 py-3 rounded-lg bg-gradient-to-br from-blue-500/10 to-blue-400/5 border border-blue-500/30 hover:border-blue-500/60 transition-all"
                    >
                      <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
                        <User size={20} className="text-white" />
                      </div>
                      <div>
                        <p className="text-sm font-bold text-foreground">{user?.name}</p>
                        <p className="text-xs text-muted-foreground">{user?.points || 0} XP</p>
                      </div>
                    </Link>
                    <Button
                      variant="ghost"
                      onClick={logout}
                      data-testid="mobile-logout-button"
                      className="w-full mt-3 text-red-400 font-bold hover:bg-red-500/10"
                    >
                      Logout
                    </Button>
                  </div>
                ) : (
                  <div className="mt-8 pt-4 border-t border-border">
                    <Link to="/auth?mode=login">
                      <Button className="w-full bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-bold rounded-lg">
                        Sign In
                      </Button>
                    </Link>
                  </div>
                )}
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="lg:pl-64 min-h-screen bg-background theme-transition">
        <div className="py-8 px-4 sm:px-6 lg:px-8">
          {children}
        </div>
      </main>

      {/* Mobile Bottom Navigation - Cozy Blue */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-card/95 border-t border-border z-50 safe-area-inset-bottom theme-transition">
        <div className="grid grid-cols-4 gap-1 px-2 py-2">
          {filteredNavItems.slice(0, 4).map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`mobile-nav-${item.label.toLowerCase().replace(' ', '-')}`}
                className={`flex flex-col items-center justify-center py-3 px-1 rounded-lg transition-all ${
                  isActive
                    ? 'bg-gradient-to-br from-blue-500/30 to-blue-400/20 text-blue-300'
                    : 'text-muted-foreground hover:text-foreground hover:bg-blue-500/10'
                }`}
              >
                <Icon size={22} className={isActive ? 'text-blue-400' : ''} />
                <span className={`text-xs mt-1 font-semibold ${isActive ? 'text-blue-300' : ''}`}>{item.label}</span>
              </Link>
            );
          })}
        </div>
      </nav>
    </div>
  );
};

export default Layout;
